import { Callback, EmberPlayerEntry, EntityId } from "game-common/models"
import { Container, Graphics, Rectangle, Text } from "pixi.js"
import { TextRenderer } from "../text/text_renderer"
import { CloseButton } from "./close_button"

// Panel geometry. Sized to the right-hand HUD gutter: wide enough for a name, a wave badge and a
// button side by side, and no wider, because everything else on that edge (the map, the roster strip)
// has to keep its own room.
const WIDTH = 186
const PAD = 8
const CORNER = 6
const HEADER_H = 18
const ROW_H = 21
const ROW_GAP = 2

// How many players get a row. The list is a glance at who is about, not a directory; the cap is set by
// the gutter the panel is docked in -- it hangs under the chips and has to stop clear of the minimap's
// slot above the roster strip -- so the tail is summarised instead (see the footer).
export const MAX_ROWS = 5
const FOOTER_H = 12

// Names are truncated by CHARACTER count rather than measured width: it is the one rule that behaves
// the same in the game and under a test runner (pixi's text metrics need a real canvas), and a name
// is a name -- there is no layout to preserve past the point it stops fitting.
export const NAME_MAX_CHARS = 13

const PANEL_COLOR = 0x05070a
const PANEL_ALPHA = 0.82
const BORDER_ALPHA = 0.36
const HEADER_COLOR = 0x8d97ad
const NAME_COLOR = 0xe6e9f0
const NAME_COLOR_HERE = 0x9fe6a8 // already in your camp -- the same green the map draws a friend in
const WAVE_COLOR = 0xffab66
const WAVE_IDLE_COLOR = 0x6b7382

// The invite button.
const BUTTON_W = 46
const BUTTON_H = 15
const BUTTON_FILL = 0x1b2030
const BUTTON_FILL_HOVER = 0x33405e
const BUTTON_RIM = 0xff8a1f
const BUTTON_RIM_IDLE = 0x8d97ad
const BUTTON_TEXT = 0xffe6b0

// How long a button stays on "Asked" after it is pressed. The invitation is a question now -- the
// answer arrives, if it arrives, as that player turning up in the camp (their row flips to "Here").
// This is only the acknowledgement that the press was heard; it lapses so the host can ask again.
export const ASKED_FEEDBACK_MS = 2500

export const truncateName = (name?: string): string => {
    if (!name) {
        return "???"
    }
    return name.length > NAME_MAX_CHARS ? name.slice(0, NAME_MAX_CHARS - 1) + "…" : name
}

// What a row says about the wave its player is on. A player who isn't in a camp at all has no wave to
// report -- distinct from one whose camp is between waves, which is wave 0 and reads as "camped".
export const waveLabel = (wave?: number): string => {
    if (wave === undefined || wave === null) {
        return "—"
    }
    return wave > 0 ? `W${wave}` : "•"
}

interface PlayerRow {
    entityId: EntityId
    container: Container
    name: Text
    wave: Text
    button: Graphics
    buttonLabel: Text
    // A player already standing in the host's camp cannot be invited into it.
    here: boolean
}

// The ember camp's "who else is playing" panel: the names of the other players logged into the
// server, the wave each one's camp is fighting off, and a button that pulls one of them into YOUR
// camp. Opened from the players chip beside the map toggle (see EmberPlayersChip) and docked to the
// right-hand edge of the screen under it.
//
// Fed by Protocol.type_updateEmberPlayers, which the server sends per-viewer with the viewer left out,
// so the panel never has to work out who "you" are. The invite is a request, not an instruction: the
// server decides whether it is allowed and where the guest lands (see the EMBER_PLAYERS campaign).
export class EmberPlayersPanel extends Container {
    private plate = new Graphics()
    private header: Text
    private footer: Text
    private rowsLayer = new Container()
    private rows: Map<EntityId, PlayerRow> = new Map()
    private onInvite: (entityId: EntityId) => void
    private players: EmberPlayerEntry[] = []
    private overflow = 0
    // "Asked" is remembered per player rather than per row, so a list that rebuilds underneath the
    // player (somebody's wave ticked over) doesn't swallow the acknowledgement they just earned.
    private sentAt: Map<EntityId, number> = new Map()

    constructor({ onInvite, onClose }: { onInvite: (entityId: EntityId) => void; onClose: Callback }) {
        super()
        this.onInvite = onInvite

        this.header = new TextRenderer().size("tiny").render("ONLINE") as Text
        this.header.tint = HEADER_COLOR
        this.header.x = PAD
        this.header.y = PAD - 2

        this.footer = new TextRenderer().size("tiny").render("") as Text
        this.footer.tint = HEADER_COLOR
        this.footer.x = PAD
        this.footer.visible = false

        this.rowsLayer.x = 0
        this.rowsLayer.y = PAD + HEADER_H

        const close = new CloseButton({ radius: 8, onClick: onClose })
        close.x = WIDTH - 6
        close.y = 6

        this.addChild(this.plate, this.header, this.rowsLayer, this.footer, close)
        this.draw()
        this.visible = false
    }

    // The players to list, newest state wins. Rows are reconciled by entity id rather than rebuilt,
    // so a list that refreshes while the pointer is over a button doesn't pull it out from under them.
    update = (players: EmberPlayerEntry[]) => {
        const listed = (players || []).slice(0, MAX_ROWS)
        this.players = listed
        this.overflow = Math.max(0, (players || []).length - listed.length)

        const seen = new Set<EntityId>()
        listed.forEach((player, index) => {
            seen.add(player.entityId)
            const row = this.rows.get(player.entityId) || this.buildRow(player)
            this.rows.set(player.entityId, row)
            this.fillRow(row, player)
            row.container.y = index * (ROW_H + ROW_GAP)
        })

        Array.from(this.rows.keys()).forEach(entityId => {
            if (!seen.has(entityId)) {
                const row = this.rows.get(entityId)!
                this.rowsLayer.removeChild(row.container)
                row.container.destroy({ children: true })
                this.rows.delete(entityId)
                // Somebody who has logged out has no press left to acknowledge.
                this.sentAt.delete(entityId)
            }
        })

        this.header.text = `ONLINE  ${(players || []).length}`
        this.footer.text = this.overflow > 0 ? `+${this.overflow} more` : ""
        this.footer.visible = this.overflow > 0
        this.footer.y = PAD + HEADER_H + listed.length * (ROW_H + ROW_GAP) + 1
        this.draw()
    }

    // Let the "Sent" acknowledgements lapse. Cheap when there are none, which is almost always.
    animate = (dtMs: number) => {
        if (this.sentAt.size < 1) {
            return
        }
        let changed = false
        this.sentAt.forEach((remaining, entityId) => {
            const left = remaining - dtMs
            if (left > 0) {
                this.sentAt.set(entityId, left)
                return
            }
            this.sentAt.delete(entityId)
            changed = true
        })
        if (changed) {
            this.players.forEach(player => {
                const row = this.rows.get(player.entityId)
                if (row) {
                    this.fillRow(row, player)
                }
            })
        }
    }

    getWidth = (): number => WIDTH
    getHeight = (): number =>
        PAD * 2 +
        HEADER_H +
        Math.max(1, this.players.length) * (ROW_H + ROW_GAP) +
        (this.overflow > 0 ? FOOTER_H : 0)

    // Press one row's invite. This IS the button's handler, and it is public because the button itself
    // is buried in the display tree -- a caller (or a test) that has the player's id has the control.
    inviteFor = (entityId: EntityId) => {
        const row = this.rows.get(entityId)
        if (!row) {
            return
        }
        // A player already in the camp has nothing to be invited to, and a press already acknowledged
        // is not sent twice -- both would just be a wasted round trip.
        if (row.here || this.sentAt.has(entityId)) {
            return
        }
        this.sentAt.set(entityId, ASKED_FEEDBACK_MS)
        this.onInvite(entityId)
        const current = this.players.find(player => player.entityId === entityId)
        if (current) {
            this.fillRow(row, current)
        }
    }

    // What a listed player's button currently says ("Invite" / "Sent" / "Here"), or undefined when they
    // have no row. The one readable summary of a row's state, for a caller outside the display tree.
    buttonLabelFor = (entityId: EntityId): string | undefined => this.rows.get(entityId)?.buttonLabel.text

    // How many players did NOT get a row (the footer's number).
    overflowCount = (): number => this.overflow

    private buildRow = (player: EmberPlayerEntry): PlayerRow => {
        const container = new Container()
        const backdrop = new Graphics()
        const name = new TextRenderer().size("tiny").render("") as Text
        const wave = new TextRenderer().size("tiny").render("") as Text
        const button = new Graphics()
        const buttonLabel = new TextRenderer().size("tiny").render("Invite") as Text
        buttonLabel.tint = BUTTON_TEXT

        name.x = PAD
        name.y = Math.round((ROW_H - 8) / 2)
        wave.y = name.y
        button.x = WIDTH - PAD - BUTTON_W
        button.y = Math.round((ROW_H - BUTTON_H) / 2)

        // The whole button is the control, and a 46x15 target is small, so its hit area is proud of
        // the fill on every side.
        button.interactive = true
        button.cursor = "pointer"
        button.hitArea = new Rectangle(-3, -3, BUTTON_W + 6, BUTTON_H + 6)

        container.addChild(backdrop, name, wave, button)
        button.addChild(buttonLabel)
        this.rowsLayer.addChild(container)

        const row: PlayerRow = {
            entityId: player.entityId,
            container,
            name,
            wave,
            button,
            buttonLabel,
            here: false,
        }

        const press = () => this.inviteFor(row.entityId)
        button.on("click", press)
        button.on("tap", press)
        button.on("mouseover", () => this.drawButton(row, true))
        button.on("mouseout", () => this.drawButton(row, false))

        // Row backdrop: a faint plate, so a long list still reads as rows rather than as a wall of
        // text. Drawn once -- it never changes size.
        backdrop.beginFill(0xffffff, 0.04)
        backdrop.drawRoundedRect(PAD - 4, 0, WIDTH - (PAD - 4) * 2, ROW_H, 3)
        backdrop.endFill()

        return row
    }

    private fillRow = (row: PlayerRow, player: EmberPlayerEntry) => {
        row.here = !!player.here
        row.name.text = truncateName(player.name)
        row.name.tint = player.here ? NAME_COLOR_HERE : NAME_COLOR
        row.wave.text = waveLabel(player.wave)
        row.wave.tint = player.wave ? WAVE_COLOR : WAVE_IDLE_COLOR
        // Right-aligned against the button, so the badge column lines up however wide a name is.
        row.wave.x = WIDTH - PAD - BUTTON_W - 6 - row.wave.width
        row.buttonLabel.text = row.here ? "Here" : this.sentAt.has(player.entityId) ? "Asked" : "Invite"
        row.buttonLabel.x = Math.round((BUTTON_W - row.buttonLabel.width) / 2)
        row.buttonLabel.y = Math.round((BUTTON_H - row.buttonLabel.height) / 2)
        this.drawButton(row, false)
    }

    private drawButton = (row: PlayerRow, hover: boolean) => {
        const inert = row.here || this.sentAt.has(row.entityId)
        row.button.clear()
        row.button.lineStyle(1, inert ? BUTTON_RIM_IDLE : BUTTON_RIM, inert ? 0.4 : hover ? 1 : 0.8)
        row.button.beginFill(hover && !inert ? BUTTON_FILL_HOVER : BUTTON_FILL, inert ? 0.5 : 0.95)
        row.button.drawRoundedRect(0, 0, BUTTON_W, BUTTON_H, 4)
        row.button.endFill()
        row.buttonLabel.alpha = inert ? 0.6 : 1
        row.button.cursor = inert ? "default" : "pointer"
    }

    private draw = () => {
        this.plate.clear()
        this.plate.beginFill(PANEL_COLOR, PANEL_ALPHA)
        this.plate.lineStyle(1, 0xffffff, BORDER_ALPHA)
        this.plate.drawRoundedRect(0, 0, WIDTH, this.getHeight(), CORNER)
        this.plate.endFill()
    }
}
